import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Filter, X, SlidersHorizontal } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    is_bestseller: searchParams.get('is_bestseller') === 'true',
    is_new: searchParams.get('is_new') === 'true',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    sort: searchParams.get('sort') || 'newest'
  });

  // Update filters when URL changes (e.g., from mobile menu navigation)
  useEffect(() => {
    setFilters({
      category: searchParams.get('category') || '',
      search: searchParams.get('search') || '',
      is_bestseller: searchParams.get('is_bestseller') === 'true',
      is_new: searchParams.get('is_new') === 'true',
      min_price: searchParams.get('min_price') || '',
      max_price: searchParams.get('max_price') || '',
      sort: searchParams.get('sort') || 'newest'
    });
  }, [searchParams]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const lang = i18n.language;
        const res = await axios.get(`${API_URL}/api/categories?lang=${lang}`);
        setCategories(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchCategories();
  }, [i18n.language]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const lang = i18n.language;
        const params = new URLSearchParams();
        if (filters.category) params.append('category', filters.category);
        if (filters.search) params.append('search', filters.search);
        if (filters.is_bestseller) params.append('is_bestseller', 'true');
        if (filters.is_new) params.append('is_new', 'true');
        if (filters.min_price) params.append('min_price', filters.min_price);
        if (filters.max_price) params.append('max_price', filters.max_price);
        params.append('lang', lang);

        const res = await axios.get(`${API_URL}/api/products?${params.toString()}`);
        let data = res.data;

        if (filters.sort === 'price_asc') {
          data.sort((a, b) => a.price_retail - b.price_retail);
        } else if (filters.sort === 'price_desc') {
          data.sort((a, b) => b.price_retail - a.price_retail);
        } else if (filters.sort === 'name') {
          data.sort((a, b) => a.name.localeCompare(b.name));
        }

        setProducts(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [filters, i18n.language]);

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== '' && v !== false) params.set(k, v.toString());
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      search: '',
      is_bestseller: false,
      is_new: false,
      min_price: '',
      max_price: '',
      sort: 'newest'
    });
    setSearchParams({});
  };

  const hasActiveFilters = filters.category || filters.search || filters.is_bestseller || filters.is_new || filters.min_price || filters.max_price;

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Categories */}
      <div>
        <h4 className="font-heading text-lg text-brand-black mb-3">{t('nav.categories')}</h4>
        <div className="space-y-2">
          <button
            onClick={() => updateFilter('category', '')}
            className={`block w-full text-left px-3 py-2 rounded transition-colors ${!filters.category ? 'bg-brand-gold/10 text-brand-gold' : 'text-brand-dark/80 hover:text-brand-black'}`}
          >
            {t('products.allCategories')}
          </button>
          {categories.map(cat => (
            <button
              key={cat.category_id}
              onClick={() => updateFilter('category', cat.slug)}
              className={`block w-full text-left px-3 py-2 rounded transition-colors ${filters.category === cat.slug || filters.category === cat.category_id ? 'bg-brand-gold/10 text-brand-gold' : 'text-brand-dark/80 hover:text-brand-black'}`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h4 className="font-heading text-lg text-brand-black mb-3">{t('products.price')} ($ CAD)</h4>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder={t('products.min')}
            value={filters.min_price}
            onChange={(e) => updateFilter('min_price', e.target.value)}
            className="bg-brand-white border-brand-gold/20 text-brand-black"
          />
          <Input
            type="number"
            placeholder={t('products.max')}
            value={filters.max_price}
            onChange={(e) => updateFilter('max_price', e.target.value)}
            className="bg-brand-white border-brand-gold/20 text-brand-black"
          />
        </div>
      </div>

      {/* Checkboxes */}
      <div className="space-y-3">
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={filters.is_bestseller}
            onCheckedChange={(checked) => updateFilter('is_bestseller', checked)}
            className="border-brand-gold/30 data-[state=checked]:bg-brand-gold data-[state=checked]:border-brand-gold"
          />
          <span className="text-brand-dark/80">{t('products.bestsellersOnly')}</span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={filters.is_new}
            onCheckedChange={(checked) => updateFilter('is_new', checked)}
            className="border-brand-gold/30 data-[state=checked]:bg-brand-gold data-[state=checked]:border-brand-gold"
          />
          <span className="text-brand-dark/80">{t('products.newOnly')}</span>
        </label>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          onClick={clearFilters}
          variant="outline"
          className="w-full border-brand-gold/30 text-brand-black hover:bg-brand-cream"
        >
          <X className="h-4 w-4 mr-2" /> {t('products.clearFilters')}
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen pt-28 pb-20 bg-brand-white" data-testid="products-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="font-accent text-brand-gold text-sm tracking-[0.3em] uppercase">{t('products.collection')}</span>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold text-brand-black mt-2">
              {filters.is_bestseller ? t('nav.bestsellers') : filters.is_new ? t('nav.newArrivals') : filters.category ? categories.find(c => c.slug === filters.category || c.category_id === filters.category)?.name || t('nav.shop') : t('products.allProducts')}
            </h1>
          </motion.div>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28 bg-brand-cream border border-brand-gold/20 rounded-lg p-6">
              <h3 className="font-heading text-xl text-brand-black mb-6 flex items-center gap-2">
                <Filter className="h-5 w-5 text-brand-gold" /> {t('products.filters')}
              </h3>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Top Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <p className="text-brand-dark/60">
                {products.length} {t('products.productsFound')}
              </p>

              <div className="flex items-center gap-4">
                {/* Mobile Filter Button */}
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden border-brand-gold/30 text-brand-black">
                      <SlidersHorizontal className="h-4 w-4 mr-2" /> {t('products.filters')}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="bg-brand-white border-brand-gold/20 w-80">
                    <SheetHeader>
                      <SheetTitle className="text-brand-black font-heading">{t('products.filters')}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterContent />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Sort */}
                <Select value={filters.sort} onValueChange={(v) => updateFilter('sort', v)}>
                  <SelectTrigger className="w-48 bg-brand-white border-brand-gold/20 text-brand-black">
                    <SelectValue placeholder={t('products.sortBy')} />
                  </SelectTrigger>
                  <SelectContent className="bg-brand-white border-brand-gold/20">
                    <SelectItem value="newest">{t('products.newest')}</SelectItem>
                    <SelectItem value="price_asc">{t('products.priceAsc')}</SelectItem>
                    <SelectItem value="price_desc">{t('products.priceDesc')}</SelectItem>
                    <SelectItem value="name">{t('products.nameAZ')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <Input
                type="text"
                placeholder={t('products.searchProduct')}
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                className="bg-brand-cream border-brand-gold/20 text-brand-black placeholder:text-brand-dark/50 py-6"
                data-testid="products-search"
              />
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[3/4] bg-brand-cream animate-pulse rounded-lg" />
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product, idx) => (
                  <ProductCard key={product.product_id} product={product} index={idx} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-brand-dark/60 text-lg">{t('products.noProducts')}</p>
                <Button onClick={clearFilters} className="mt-4 bg-brand-gold text-white hover:bg-brand-gold">
                  {t('products.resetFilters')}
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
